// pages/UserAdmin/crm/ContactDrawer.js
//
// MODAL centralizado com o detalhe do contato. Substitui a navegação
// "abrir detalhes → outra rota" que existia antes — o supervisor filtra
// a lista, clica na row, vê tudo do contato e volta sem sair da página.
//
// Era um drawer lateral de 488px até set/26. Virou modal porque a tabela
// ganhou colunas (Situação) e, com o drawer aberto, as colunas restantes
// espremiam a ponto de o nome do contato quebrar em três linhas. O modal
// ainda abre espaço pras funcionalidades que vêm depois — o drawer já
// estava no limite de largura útil.
//
// Out/26 (CRM moderno, Claude Design "CRM Moderno.dc.html"): 900px, cantos
// de 22, o brilho verde no canto, "Abrir conversa" com nome, e o Perfil em
// duas colunas — dados e campos à esquerda; último template e Lembretes à
// direita. "Lembretes" deixou de ser o aviso "na próxima atualização": lista
// o que está marcado com o contato na Agenda e marca retorno ou agenda envio
// (crm/LembretesContato). Abaixo de 820px vira uma coluna; no celular, tela
// cheia. Os seletores são <select> nativos (teclado e leitor de tela de
// graça, e a lista do celular é a do sistema).
//
// 5 abas:
//   Perfil             → dados, interesse, reunião, agente, campos, lembretes.
//   Etiquetas & Pastas → editor rápido com pills clicáveis.
//   Anotações          → lista + composer.
//   Histórico          → timeline. Nesta rodada só mostra a última
//                        mensagem; audit log (mudanças de status,
//                        atribuições) fica pra PR futuro.
//   Formulários        → o que o contato respondeu (Formulários PR 2).
//
// Sem CSS-in-JS externo — só `sx` do MUI, mesmo padrão do resto do sistema.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Dialog, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  FiCalendar, FiCheck, FiChevronDown, FiEdit2, FiFileText, FiFolder, FiMessageCircle, FiPlus, FiTrash2, FiX,
} from 'react-icons/fi';
import {
  CRM_THEME, INTEREST_LABELS, MEETING_LABELS, MEETING_OPTS, OUTFIT, SYNE, UNSET,
  avatarDe, folderColor, formatarFone, initials, labelColor, rotulo,
} from './theme';
import SilenceBadge, { isSilent } from './SilenceBadge';
import { PilulaInteresse } from './TabelaContatos';
import { ItemMenu, MenuCrm, NotaMenu } from './MenuCrm';
import LembretesContato from './LembretesContato';
import OrigemContato from './OrigemContato';
import QualificacaoContato from './QualificacaoContato';
import api from '../../../services/Api';
import RespostasLista from '../../formularios/RespostasLista';

// Formulários PR 2 (out/26): a paleta do CRM pras respostas.
const CORES_FORM = {
  texto: CRM_THEME.text, texto2: CRM_THEME.text2, texto3: CRM_THEME.text3,
  borda: CRM_THEME.border, fundo: CRM_THEME.inset, verde: CRM_THEME.brandDark, ambar: 'var(--ch-amber-d)',
};

// Níveis no <select>: "Não classificado" primeiro, como nos filtros. É
// string porque `<select value={null}>` vira campo não-controlado; no banco
// é NULL (models.INTEREST_UNSET).
const INTEREST_OPTS = [UNSET, 'ALTO', 'MEDIO', 'BAIXO', 'FRIO'];

const ABAS = [
  ['perfil', 'Perfil'], ['labels', 'Etiquetas & Pastas'], ['notas', 'Anotações'],
  ['hist', 'Histórico'], ['forms', 'Formulários'],
];

// Abaixo de 820px o Perfil vira uma coluna; abaixo de 560, o rótulo do
// campo sobe pra cima do valor.
const UMA_COLUNA = '@media (max-width: 819.98px)';
const ESTREITO = '@media (max-width: 559.98px)';

const foco = { '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2 } };

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const day = String(d.getDate()).padStart(2, '0');
    const month = d.toLocaleString('pt-BR', { month: 'short' }).replace('.', '');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${day} ${month} · ${hh}:${mm}`;
  } catch {
    return '—';
  }
}

function Secao({ children, hint, mb = '4px' }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap', mb }}>
      <Box component="span" sx={rotulo}>{children}</Box>
      {hint && <Box component="span" sx={{ fontSize: 13, color: 'var(--cx-cap)' }}>— {hint}</Box>}
    </Box>
  );
}

// Uma linha de "rótulo | valor" da coluna de dados.
function Linha({ rotulo: r, id, ultima, children }) {
  return (
    <Box sx={{
      display: 'grid', gridTemplateColumns: '172px minmax(0,1fr)', alignItems: 'center', gap: '12px', minHeight: 56,
      borderBottom: ultima ? 0 : '1px solid var(--ch-bdr)',
      [ESTREITO]: { gridTemplateColumns: 'minmax(0,1fr)', gap: '6px', py: '10px' },
    }}>
      <Box component={id ? 'label' : 'span'} htmlFor={id} sx={{ fontSize: 14.5, color: 'var(--ch-text2)' }}>{r}</Box>
      {children}
    </Box>
  );
}

const campoBase = {
  width: '100%', height: 40, boxSizing: 'border-box', borderRadius: '10px', bgcolor: 'var(--cx-input)', color: 'var(--ch-text)',
  fontFamily: OUTFIT, fontSize: 14.5, outline: 0,
  '&:focus-visible': { borderColor: 'var(--ch-green)', boxShadow: '0 0 0 3px var(--cx-glow)' },
  '&:disabled': { opacity: 0.6 },
};

function Seletor({ id, value, onChange, disabled, children }) {
  return (
    <Box component="span" sx={{ position: 'relative', display: 'flex', minWidth: 0 }}>
      <Box component="select" id={id} className="cx-campo-nativo" value={value} onChange={onChange} disabled={disabled}
        sx={{ ...campoBase, appearance: 'none', WebkitAppearance: 'none', p: '0 36px 0 12px', border: '1px solid var(--ch-bdr)', cursor: 'pointer' }}>
        {children}
      </Box>
      <Box component="span" aria-hidden sx={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', display: 'flex', color: 'var(--cx-cap)', pointerEvents: 'none' }}>
        <FiChevronDown size={16} />
      </Box>
    </Box>
  );
}

// Uma linha de campo personalizado. TOTALMENTE controlada pelo drawer: o
// rascunho do que foi digitado vive lá em cima, não aqui.
//
// Antes o valor morava aqui e era gravado no blur. Dois defeitos em
// produção (out/26) vieram disso:
//   1. Escape ou clique fora fecham o modal SEM passar pelo blur, então o
//      que a pessoa digitou sumia sem nenhum aviso — o pior tipo de perda,
//      porque parecia salvo;
//   2. `onKeyDown` no MUI TextField cai no <div> que embrulha o campo, não
//      no <input>, então o `blur()` do Enter era inócuo e o Enter também
//      não gravava. (Hoje é <input> nativo: o Enter cai nele.)
// Com o rascunho no drawer, ele sabe o que está pendente, mostra o botão
// de salvar e grava antes de fechar por qualquer caminho.
function CustomFieldRow({ field, value, dirty, disabled, onChange, onCommit, ultima }) {
  // O tipo escolhe o teclado e a validação do navegador; o valor viaja
  // como texto de qualquer forma (ver models.CUSTOM_FIELD_TYPES).
  const inputType = field.field_type === 'DATE' ? 'date' : 'text';
  const placeholder = field.field_type === 'CURRENCY' ? 'Ex.: 250,00'
    : field.field_type === 'NUMBER' ? 'Ex.: 3'
      : field.field_type === 'DATE' ? ''
        : '—';
  const id = `cf-${field.key}`;
  return (
    <Linha rotulo={field.label} id={id} ultima={ultima}>
      <Box component="input" id={id} type={inputType} className="cx-campo-nativo"
        value={value} placeholder={placeholder} maxLength={300} disabled={disabled}
        inputMode={field.field_type === 'NUMBER' || field.field_type === 'CURRENCY' ? 'decimal' : undefined}
        onChange={(e) => onChange(field.key, e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onCommit(); } }}
        sx={{
          ...campoBase, p: '0 12px',
          // Borda âmbar marca o que ainda não foi gravado. Sem sinal
          // visual, "não salvo" é indistinguível de "salvo".
          border: dirty ? '2px solid var(--ch-amber)' : '1px solid var(--ch-bdr)',
          '&::placeholder': { color: 'var(--cx-cap)', opacity: 1 },
        }} />
    </Linha>
  );
}

function BotaoQuadrado({ titulo, onClick, children }) {
  return (
    <Box component="button" type="button" title={titulo} aria-label={titulo} onClick={onClick}
      sx={{
        ...foco, width: 40, height: 40, p: 0, flexShrink: 0, borderRadius: '12px', border: '1px solid var(--ch-bdr)', bgcolor: 'var(--ch-card)',
        color: 'var(--ch-text2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        transition: 'border-color .15s, color .15s', '&:hover': { borderColor: 'var(--ch-bdr2)', color: 'var(--ch-text)' },
      }}>
      {children}
    </Box>
  );
}

function BotaoAdicionar({ onClick, children, pasta }) {
  return (
    <Box component="button" type="button" onClick={onClick} aria-haspopup="menu"
      sx={{
        ...foco, height: 32, display: 'inline-flex', alignItems: 'center', gap: '6px', px: '12px', cursor: 'pointer',
        borderRadius: pasta ? '10px' : '999px', bgcolor: 'transparent', color: 'var(--cx-cap)', border: '1px dashed var(--ch-bdr2)',
        fontFamily: OUTFIT, fontSize: 14, fontWeight: 500, transition: 'border-color .15s, color .15s',
        '&:hover': { borderColor: 'var(--ch-green)', color: 'var(--ch-green-d)' },
      }}>
      <FiPlus size={14} />{children}
    </Box>
  );
}

function Pilula({ alto = 26, cores, tracejado, children }) {
  return (
    <Box component="span" sx={{
      height: alto, display: 'inline-flex', alignItems: 'center', gap: '6px', px: '10px', borderRadius: '999px',
      bgcolor: cores.bg, color: cores.color, border: `1px ${tracejado ? 'dashed' : 'solid'} ${cores.border}`,
      fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {children}
    </Box>
  );
}

const textoVazio = { py: 3, fontSize: 14.5, color: 'var(--cx-cap)', textAlign: 'center' };
const cartao = { p: '14px 16px', borderRadius: '14px', bgcolor: 'var(--ch-surf)', border: '1px solid var(--ch-bdr)' };

export default function ContactDrawer({
  open,
  contact,
  notes = [],
  notesLoading = false,
  timeline = [],                   // últimas mensagens ordenadas desc
  saving = false,
  isSupervisor = false,
  onClose,
  onOpenChat,                      // (contact) => void
  onChangeInterest,                // (contact, newValue) => Promise<void>
  onChangeMeeting,                 // (contact, newValue) => Promise<void>
  onAddNote,                       // (text) => Promise<void>
  onDeleteNote,                    // (noteId) => void
  onEdit,                          // (contact) => void — abre editar nome/telefone
  allLabels = [],                  // etiquetas da company, pro seletor
  allFolders = [],                 // pastas da company, pro seletor
  onAttachLabel,                   // (labelId) => Promise<void>
  onAttachFolder,                  // (folderId) => Promise<void>
  agents = [],                     // agentes da company (só chega pra supervisor)
  onAssignAgent,                   // (agentId | null, agentName | null) => void
  onDetachLabel,                   // (labelId) => void
  onDetachFolder,                  // (folderId) => void
  onSaveCustomFields,              // (contact, {key: value}) => Promise<void>
  token,                           // JWT — os Lembretes agendam envio com ele
  onQualificado,                   // (contactId, qualificacao | null) => void
}) {
  const navigate = useNavigate();
  // No celular a janela ocupa a tela toda: 86vh de um modal centralizado
  // num aparelho de 390px sobravam ~300px úteis entre cabeçalho e rodapé.
  const telaCheia = useMediaQuery('(max-width:599.98px)');
  const [tab, setTab] = useState('perfil');
  // Rascunho dos campos personalizados: só as chaves que foram tocadas.
  // Mora aqui, e não em cada linha, pra o modal saber o que está pendente
  // e poder gravar antes de fechar por qualquer caminho.
  const [drafts, setDrafts] = useState({});
  const [savingFields, setSavingFields] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [showNoteActions, setShowNoteActions] = useState(false);
  // Âncoras dos seletores de etiqueta/pasta. Até set/26 os chips "+
  // Etiqueta"/"+ Pasta" chamavam `onOpenLabelsEditor`, que no
  // Contacts.js apontava pro diálogo de EDITAR NOME E TELEFONE — os dois
  // handlers eram placeholder e nunca foram religados. Agora cada chip
  // abre o próprio menu com o que a company tem cadastrado.
  const [labelAnchor, setLabelAnchor] = useState(null);
  const [folderAnchor, setFolderAnchor] = useState(null);
  // Formulários respondidos por este contato (Formulários PR 2).
  const [formularios, setFormularios] = useState(null);
  const abasRef = useRef([]);

  useEffect(() => {
    if (!open || !contact?.id) return undefined;
    let vivo = true;
    setFormularios(null);
    api.get(`/formularios/contato/${contact.id}`)
      .then((r) => { if (vivo) setFormularios(r.data?.items || []); })
      .catch(() => { if (vivo) setFormularios([]); });
    return () => { vivo = false; };
  }, [open, contact?.id]);

  useEffect(() => {
    // Reset ao trocar de contato — sempre volta pra aba Perfil.
    setTab('perfil');
    setNoteText('');
    setShowNoteActions(false);
    setLabelAnchor(null);
    setFolderAnchor(null);
    setDrafts({});
  }, [contact?.id]);

  // ─── Campos personalizados: rascunho, pendência e gravação ────────────
  // Declarados antes do early-return de `!contact` porque hook não pode
  // ficar depois de um return condicional.
  const storedFields = contact?.custom_fields || [];
  const dirtyKeys = storedFields
    .filter((f) => drafts[f.key] !== undefined && drafts[f.key] !== (f.value || ''))
    .map((f) => f.key);
  const hasUnsaved = dirtyKeys.length > 0;

  const changeDraft = (key, value) =>
    setDrafts((prev) => ({ ...prev, [key]: value }));

  const saveFields = useCallback(async () => {
    const pendentes = (contact?.custom_fields || []).filter(
      (f) => drafts[f.key] !== undefined && drafts[f.key] !== (f.value || '')
    );
    if (!pendentes.length || savingFields) return;
    // Um PATCH com tudo, não um por campo: o backend mescla dicionário
    // parcial, então mandar junto é uma requisição em vez de N.
    const payload = Object.fromEntries(
      pendentes.map((f) => [f.key, (drafts[f.key] || '').trim()])
    );
    setSavingFields(true);
    try {
      await onSaveCustomFields?.(contact, payload);
      setDrafts({});
    } catch {
      // Quem chamou já avisou o usuário. Aqui o importante é NÃO limpar o
      // rascunho: se a rede caiu, o que a pessoa digitou continua na tela
      // pra ela tentar de novo, em vez de sumir.
    } finally {
      setSavingFields(false);
    }
  }, [contact, drafts, savingFields, onSaveCustomFields]);

  // Fechar por QUALQUER caminho (X, Escape, clique fora) grava o que está
  // pendente antes de sair. É o que impede a perda silenciosa que o botão
  // de salvar sozinho não cobriria — ninguém clica em salvar num modal que
  // já acha que salvou.
  const closeSaving = useCallback(() => {
    if (hasUnsaved) saveFields();
    onClose?.();
  }, [hasUnsaved, saveFields, onClose]);

  // Sair pra Agenda (clique num compromisso dos Lembretes) também grava o
  // pendente — trocar de rota desmonta o modal sem passar pelo onClose.
  const irPara = useCallback((url) => {
    if (hasUnsaved) saveFields();
    navigate(url);
  }, [hasUnsaved, saveFields, navigate]);

  if (!contact) return null;

  // "Respondeu diferente do CRM": o formulário NÃO sobrescreve o que o CRM
  // já tem (services/formulario_envio). A diferença aparece aqui pra alguém
  // decidir — "Usar a resposta" põe o valor no rascunho do campo, e ele é
  // gravado como qualquer edição. A resposta mais recente vence; o que já
  // foi igualado some.
  const divergencias = [];
  const vistas = new Set();
  for (const f of formularios || []) {
    for (const m of f.crm?.mantidos || []) {
      const chave = m.chave || `nome:${m.rotulo}`;
      if (vistas.has(chave)) continue;
      vistas.add(chave);
      const atual = m.chave
        ? (drafts[m.chave] ?? (storedFields.find((x) => x.key === m.chave)?.value || ''))
        : (contact.name || '');
      if ((atual || '').trim() === (m.resposta || '').trim()) continue;
      divergencias.push({ ...m, formulario: f.flow_nome, temCampo: Boolean(m.chave && storedFields.some((x) => x.key === m.chave)) });
    }
  }
  const respondidos = (formularios || []).filter((f) => f.respondido_em).length;

  // Só o que ainda não está no contato aparece no seletor.
  const attachedLabelIds = new Set((contact.labels || []).map(l => l.id));
  const attachedFolderIds = new Set((contact.folders || []).map(f => f.id));
  const availableLabels = allLabels.filter(l => !attachedLabelIds.has(l.id));
  const availableFolders = allFolders.filter(f => !attachedFolderIds.has(f.id));

  const silent = isSilent(contact);
  const mCfg = CRM_THEME.meeting[contact.meeting_status] || CRM_THEME.meeting.NAO_AGENDADA;
  const av = avatarDe(contact.id);

  const saveNote = async () => {
    const t = noteText.trim();
    if (!t) return;
    await onAddNote?.(t);
    setNoteText('');
    setShowNoteActions(false);
  };

  // Setas trocam de aba, como em qualquer lista de abas.
  const teclaAba = (e, i) => {
    const n = ABAS.length;
    const prox = e.key === 'ArrowRight' ? (i + 1) % n : e.key === 'ArrowLeft' ? (i - 1 + n) % n
      : e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : null;
    if (prox === null) return;
    e.preventDefault();
    setTab(ABAS[prox][0]);
    abasRef.current[prox]?.focus();
  };

  const statusRodape = savingFields
    ? 'Salvando…'
    : hasUnsaved
      ? `${dirtyKeys.length} alteração${dirtyKeys.length > 1 ? 'ões' : ''} não salva${dirtyKeys.length > 1 ? 's' : ''}`
      : 'Tudo salvo';

  return (
    <Dialog
      open={!!open}
      onClose={closeSaving}
      fullScreen={telaCheia}
      maxWidth={false}
      aria-labelledby="crm-contato-nome"
      PaperProps={{
        'data-screen-label': 'Contato',
        sx: {
          width: '100%', maxWidth: telaCheia ? 'none' : 900,
          // Altura fixa e não `auto`: as abas têm conteúdos de tamanhos bem
          // diferentes (Perfil é curto, Histórico é longo), e sem isso o
          // modal saltava de tamanho a cada troca de aba. O scroll fica no
          // corpo.
          height: telaCheia ? '100%' : 'min(86vh, 720px)',
          m: telaCheia ? 0 : 3,
          borderRadius: telaCheia ? 0 : '22px',
          border: telaCheia ? 0 : '1px solid var(--ch-bdr)',
          bgcolor: 'var(--ch-card)', color: 'var(--ch-text)', fontFamily: OUTFIT,
          display: 'flex', flexDirection: 'column', overflow: 'hidden', isolation: 'isolate', position: 'relative',
        },
      }}
    >
      <Box aria-hidden sx={{
        position: 'absolute', zIndex: -1, top: -180, left: -120, width: 560, height: 340, pointerEvents: 'none',
        background: 'radial-gradient(closest-side, var(--cx-glow), transparent)',
      }} />

      {/* Cabeçalho */}
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'flex-start', gap: '16px', p: '22px 22px 18px 24px', flexWrap: 'wrap', [ESTREITO]: { p: '16px 16px 14px', gap: '12px' } }}>
        <Box component="span" aria-hidden sx={{
          width: 56, height: 56, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: av.bg, color: av.fg, fontFamily: SYNE, fontSize: 21, fontWeight: 800,
          [ESTREITO]: { width: 46, height: 46, fontSize: 18 },
        }}>
          {initials(contact.name)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <Box component="h2" id="crm-contato-nome" sx={{ m: 0, fontFamily: SYNE, fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.15, overflowWrap: 'anywhere', [ESTREITO]: { fontSize: 19 } }}>
              {contact.name}
            </Box>
            {/* Fallback é UNSET, não MEDIO: `interest_status` null significa
                que ninguém classificou, e mostrar "Médio" recriaria no visual
                o default que foi removido do banco em out/26. */}
            <PilulaInteresse nivel={contact.interest_status} alto={26} />
          </Box>
          <Box component="span" sx={{ fontSize: 15, color: 'var(--ch-text2)', fontVariantNumeric: 'tabular-nums' }}>
            {formatarFone(contact.phone_number)}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', mt: '2px' }}>
            <Pilula cores={mCfg}>
              <FiCalendar size={13} />
              {MEETING_LABELS[contact.meeting_status] || 'Não agendada'}
            </Pilula>
            {silent && <SilenceBadge days={contact.days_since_last_inbound} alto={26} />}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', [ESTREITO]: { width: '100%' } }}>
          {/* Editar nome/telefone. Antes só dava pra chegar aqui pelos
              chips "+ Etiqueta"/"+ Pasta", que abriam este diálogo por
              engano — ou seja, a ação existia mas escondida no lugar
              errado. Agora tem botão próprio. */}
          <BotaoQuadrado titulo="Editar contato" onClick={() => onEdit?.(contact)}><FiEdit2 size={16} /></BotaoQuadrado>
          <Box component="button" type="button" title="Abrir conversa" onClick={() => onOpenChat?.(contact)}
            sx={{
              ...foco, height: 40, pl: '14px', pr: '16px', borderRadius: '12px', border: '1px solid var(--ch-green-b)',
              bgcolor: 'var(--ch-green-bg)', color: 'var(--ch-green-d)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              fontFamily: OUTFIT, fontSize: 14.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'border-color .15s',
              '&:hover': { borderColor: 'var(--ch-green)' },
              [ESTREITO]: { flex: 1 },
            }}>
            <FiMessageCircle size={16} />Abrir conversa
          </Box>
          <BotaoQuadrado titulo="Fechar" onClick={closeSaving}><FiX size={17} /></BotaoQuadrado>
        </Box>
      </Box>

      {/* Abas */}
      <Box role="tablist" aria-label="Seções do contato" sx={{
        flexShrink: 0, display: 'flex', gap: '4px', px: '16px', borderBottom: '1px solid var(--ch-bdr)',
        overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
      }}>
        {ABAS.map(([k, label], i) => {
          const on = tab === k;
          const n = k === 'forms' ? respondidos : 0;
          return (
            <Box key={k} component="button" type="button" role="tab" id={`crm-aba-${k}`} aria-selected={on} aria-controls="crm-aba-painel"
              tabIndex={on ? 0 : -1} ref={(el) => { abasRef.current[i] = el; }}
              aria-label={n > 0 ? `${label} (${n})` : undefined}
              onClick={() => setTab(k)} onKeyDown={(e) => teclaAba(e, i)}
              sx={{
                ...foco, flexShrink: 0, height: 46, display: 'inline-flex', alignItems: 'center', gap: '7px', px: '10px',
                border: 0, bgcolor: 'transparent', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: OUTFIT, fontSize: 14.5,
                color: on ? 'var(--ch-text)' : 'var(--cx-cap)', fontWeight: on ? 600 : 500,
                boxShadow: on ? 'inset 0 -2px 0 var(--ch-green)' : 'none', transition: 'color .15s',
                '&:hover': { color: 'var(--ch-text)' }, '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: -2 },
              }}>
              {label}
              {n > 0 && (
                <Box component="span" sx={{ minWidth: 20, height: 20, px: '6px', borderRadius: '999px', bgcolor: 'var(--ch-green-bg)', color: 'var(--ch-green-d)', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  {n}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Corpo (scroll interno) */}
      <Box id="crm-aba-painel" role="tabpanel" aria-labelledby={`crm-aba-${tab}`}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '24px', scrollbarWidth: 'thin', scrollbarColor: 'var(--ch-bdr2) transparent', [ESTREITO]: { p: '16px' } }}>

        {tab === 'perfil' && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.45fr) minmax(0,1fr)', gap: '28px', alignItems: 'start', [UMA_COLUNA]: { gridTemplateColumns: 'minmax(0,1fr)' } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', minWidth: 0 }}>
              <Box>
                <Secao>Dados do contato</Secao>
                {/* Agente responsável — era só leitura até out/26: pra
                    trocar, o supervisor tinha que fechar o modal, marcar o
                    contato na lista e usar a barra de ação em lote.
                    Agente comum continua vendo texto: `GET /users/agents`
                    é supervisor-only e a lista chega vazia pra ele. */}
                <Linha rotulo="Agente responsável" id={isSupervisor ? 'crm-agente' : undefined}>
                  {isSupervisor ? (
                    <Seletor id="crm-agente" value={contact.assigned_user_id || ''} disabled={saving}
                      onChange={(e) => {
                        const id = e.target.value || null;
                        onAssignAgent?.(id, id ? (agents.find(a => a.id === id)?.name || null) : null);
                      }}>
                      <option value="">Sem responsável</option>
                      {/* O atual pode ter saído da lista (agente desativado):
                          continua aparecendo, senão o <select> mostraria
                          "Sem responsável" sem ser verdade. */}
                      {contact.assigned_user_id && !agents.some(a => a.id === contact.assigned_user_id) && (
                        <option value={contact.assigned_user_id}>{contact.assigned_user_name || 'Agente'}</option>
                      )}
                      {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </Seletor>
                  ) : (
                    <Box component="span" sx={{ fontSize: 14.5, pl: '13px', color: contact.assigned_user_name ? 'var(--ch-text)' : 'var(--cx-cap)', [ESTREITO]: { pl: 0 } }}>
                      {contact.assigned_user_name || 'Sem agente'}
                    </Box>
                  )}
                </Linha>
                <Linha rotulo="Nível de interesse" id="crm-interesse">
                  <Seletor id="crm-interesse" value={contact.interest_status || UNSET} disabled={saving}
                    onChange={(e) => onChangeInterest?.(contact, e.target.value)}>
                    {INTEREST_OPTS.map(k => <option key={k} value={k}>{INTEREST_LABELS[k]}</option>)}
                  </Seletor>
                </Linha>
                <Linha rotulo="Status de reunião" id="crm-reuniao">
                  <Seletor id="crm-reuniao" value={contact.meeting_status || 'NAO_AGENDADA'} disabled={saving}
                    onChange={(e) => onChangeMeeting?.(contact, e.target.value)}>
                    {MEETING_OPTS.map(k => <option key={k} value={k}>{MEETING_LABELS[k]}</option>)}
                  </Seletor>
                </Linha>
                <Linha rotulo="Última interação" ultima>
                  <Box component="span" sx={{ fontSize: 14.5, pl: '13px', fontVariantNumeric: 'tabular-nums', [ESTREITO]: { pl: 0 } }}>
                    {fmtDate(contact.last_interaction)}
                  </Box>
                </Linha>
              </Box>

              {divergencias.length > 0 && (
                <Box role="note" sx={{ p: '14px 14px 14px 16px', borderRadius: '14px', bgcolor: 'var(--ch-amber-bg)', border: '1px solid var(--ch-amber-b)', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <Box component="span" sx={{ fontSize: 14.5, fontWeight: 600, color: 'var(--ch-amber-d)' }}>
                    Respondeu diferente do CRM
                  </Box>
                  {divergencias.map((d) => (
                    <Box key={d.chave || d.rotulo} sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                      <Box component="span" sx={{ flex: 1, minWidth: 220, fontSize: 14, lineHeight: 1.5, color: 'var(--ch-text2)', overflowWrap: 'anywhere' }}>
                        <Box component="b" sx={{ color: 'var(--ch-text)', fontWeight: 600 }}>{d.rotulo}</Box>: no formulário{d.formulario ? ` “${d.formulario}”` : ''} respondeu “{d.resposta}”; o CRM tem “{d.crm}”.
                      </Box>
                      {(d.temCampo || !d.chave) && (
                        <Box component="button" type="button"
                          onClick={() => (d.temCampo ? changeDraft(d.chave, d.resposta) : onEdit?.(contact))}
                          disabled={d.temCampo && (saving || savingFields)}
                          aria-label={d.temCampo ? `Usar a resposta em ${d.rotulo}` : undefined}
                          sx={{
                            ...foco, height: 34, px: '14px', borderRadius: '999px', border: '1px solid var(--ch-amber-b)', bgcolor: 'var(--ch-card)',
                            color: 'var(--ch-amber-d)', fontFamily: OUTFIT, fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                            '&:disabled': { opacity: 0.6, cursor: 'default' },
                          }}>
                          {d.temCampo ? 'Usar a resposta' : 'Editar nome'}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              {/* Campos personalizados (out/26). A lista vem do contato, já
                  ordenada e com rótulo — ver _serialize_custom_fields no
                  backend. Vazia até alguém criar o primeiro campo. */}
              {(contact.custom_fields?.length > 0 || isSupervisor) && (
                <Box>
                  <Secao>Campos personalizados</Secao>
                  {contact.custom_fields?.length > 0 ? (
                    contact.custom_fields.map((field, i) => (
                      <CustomFieldRow
                        key={field.key}
                        field={field}
                        value={drafts[field.key] ?? (field.value || '')}
                        dirty={dirtyKeys.includes(field.key)}
                        disabled={saving || savingFields}
                        onChange={changeDraft}
                        onCommit={saveFields}
                        ultima={i === contact.custom_fields.length - 1}
                      />
                    ))
                  ) : (
                    <Box sx={{ mt: '8px', p: '14px 16px', borderRadius: '14px', border: '1px dashed var(--ch-bdr2)', color: 'var(--cx-cap)', fontSize: 14, lineHeight: 1.5 }}>
                      Nenhum campo criado ainda. Use “Campos personalizados” na
                      barra do CRM para definir o que a sua empresa guarda em
                      cada contato.
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', minWidth: 0 }}>
              {/* Qualificação (out/26, rastreamento PR 2): qualificado,
                  desqualificado ou venda fechada. */}
              <QualificacaoContato key={`q-${contact.id}`} contato={contact} cartao={cartao} fmtDate={fmtDate}
                onMudou={onQualificado} />

              {/* Origem (out/26, rastreamento PR 1): de qual anúncio veio. */}
              <OrigemContato key={contact.id} contato={contact} cartao={cartao} fmtDate={fmtDate} />

              {contact.last_template && (
                <Box>
                  <Secao mb="12px">Último template</Secao>
                  <Box sx={{ ...cartao, display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Box component="span" aria-hidden sx={{ width: 36, height: 36, borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'var(--ch-blue-bg)', color: 'var(--ch-blue-d)', border: '1px solid var(--ch-blue-b)' }}>
                      <FiFileText size={16} />
                    </Box>
                    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <Box component="span" sx={{ fontSize: 14.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {contact.last_template.name || '—'}
                      </Box>
                      <Box component="span" sx={{ fontSize: 13, color: 'var(--cx-cap)', fontVariantNumeric: 'tabular-nums' }}>
                        {fmtDate(contact.last_template.timestamp)}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}

              <LembretesContato contato={contact} token={token} onAbrirAgenda={irPara} />
            </Box>
          </Box>
        )}

        {tab === 'labels' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
            <Box>
              <Secao hint="sinal rápido, semântica OR nos filtros" mb="12px">Etiquetas</Secao>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {(contact.labels || []).map(lb => {
                  const c = labelColor(lb.color);
                  return (
                    <Box key={lb.id} component="span" sx={{ height: 32, display: 'inline-flex', alignItems: 'center', gap: '6px', pl: '12px', pr: '6px', borderRadius: '999px', bgcolor: c.bg, color: c.color, border: `1px solid ${c.border}`, fontSize: 14, fontWeight: 600 }}>
                      {lb.name}
                      <Box component="button" type="button" onClick={() => onDetachLabel?.(lb.id)} aria-label={`Remover ${lb.name}`}
                        sx={{ ...foco, width: 22, height: 22, p: 0, border: 0, borderRadius: '999px', bgcolor: 'transparent', color: 'inherit', opacity: 0.7, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { opacity: 1 } }}>
                        <FiX size={13} strokeWidth={2.4} />
                      </Box>
                    </Box>
                  );
                })}
                <BotaoAdicionar onClick={(e) => setLabelAnchor(e.currentTarget)}>Etiqueta</BotaoAdicionar>
              </Box>
            </Box>

            <Box>
              <Secao hint="agrupamento estrutural" mb="12px">Pastas</Secao>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {(contact.folders || []).map(fd => {
                  const c = folderColor(fd.color);
                  return (
                    <Box key={fd.id} component="span" sx={{ height: 32, display: 'inline-flex', alignItems: 'center', gap: '7px', pl: '11px', pr: '6px', borderRadius: '10px', bgcolor: 'var(--ch-surf)', color: c.color, border: `1px solid ${c.border}`, fontSize: 14, fontWeight: 600 }}>
                      <FiFolder size={14} />
                      {fd.name}
                      <Box component="button" type="button" onClick={() => onDetachFolder?.(fd.id)} aria-label={`Tirar da pasta ${fd.name}`}
                        sx={{ ...foco, width: 22, height: 22, p: 0, border: 0, borderRadius: '999px', bgcolor: 'transparent', color: 'inherit', opacity: 0.7, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { opacity: 1 } }}>
                        <FiX size={13} strokeWidth={2.4} />
                      </Box>
                    </Box>
                  );
                })}
                <BotaoAdicionar pasta onClick={(e) => setFolderAnchor(e.currentTarget)}>Pasta</BotaoAdicionar>
              </Box>
              <Box sx={{ ...cartao, mt: '16px' }}>
                <Box sx={{ ...rotulo, mb: '6px' }}>Diferença</Box>
                <Box sx={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ch-text2)' }}>
                  Etiquetas usam cores <Box component="strong" sx={{ color: 'var(--ch-text)' }}>saturadas</Box> (sinal). Pastas usam tons <Box component="strong" sx={{ color: 'var(--ch-text)' }}>pastel</Box> com ícone (estrutura). Os dois convivem na row sem competir.
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {tab === 'notas' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Secao hint="alimentam o contexto da IA quando ativa" mb="2px">Anotações</Secao>

            {notesLoading ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CircularProgress size={18} sx={{ color: 'var(--ch-green)' }} />
              </Box>
            ) : (
              (notes || []).map(n => (
                <Box key={n.id} sx={{ ...cartao, display: 'flex', flexDirection: 'column', gap: '6px', '&:hover .note-del, &:focus-within .note-del': { opacity: 1 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Box component="span" sx={{ fontSize: 14, fontWeight: 600, color: 'var(--ch-text2)' }}>{n.author_name || 'Sistema'}</Box>
                    <Box component="span" sx={{ fontSize: 13, color: 'var(--cx-cap)', fontVariantNumeric: 'tabular-nums', ml: 'auto' }}>{fmtDate(n.created_at)}</Box>
                    <Box component="button" type="button" className="note-del" onClick={() => onDeleteNote?.(n.id)} aria-label="Excluir anotação" title="Excluir anotação"
                      sx={{
                        ...foco, width: 28, height: 28, p: 0, border: 0, borderRadius: '8px', bgcolor: 'transparent', color: 'var(--cx-cap)', opacity: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'opacity .15s',
                        '&:hover': { bgcolor: 'var(--ch-red-bg)', color: 'var(--ch-red-d)' }, '@media (hover: none)': { opacity: 1 },
                      }}>
                      <FiTrash2 size={14} />
                    </Box>
                  </Box>
                  <Box sx={{ fontSize: 15, lineHeight: 1.55, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{n.text}</Box>
                </Box>
              ))
            )}

            <Box component="textarea"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onFocus={() => setShowNoteActions(true)}
              placeholder="Adicionar anotação…"
              aria-label="Adicionar anotação"
              rows={3}
              sx={{
                mt: '4px', width: '100%', boxSizing: 'border-box', minHeight: 96, resize: 'vertical', p: '12px 14px', borderRadius: '14px',
                border: '1px solid var(--ch-bdr)', bgcolor: 'var(--cx-input)', color: 'var(--ch-text)', fontFamily: OUTFIT, fontSize: 15, lineHeight: 1.5, outline: 0,
                '&::placeholder': { color: 'var(--cx-cap)', opacity: 1 },
                '&:focus-visible': { borderColor: 'var(--ch-green)', boxShadow: '0 0 0 3px var(--cx-glow)' },
              }}
            />
            {showNoteActions && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <Box component="button" type="button" onClick={() => { setNoteText(''); setShowNoteActions(false); }}
                  sx={{ ...foco, height: 38, px: '16px', borderRadius: '999px', border: 0, bgcolor: 'transparent', color: 'var(--ch-text2)', fontFamily: OUTFIT, fontSize: 14.5, fontWeight: 500, cursor: 'pointer' }}>
                  Cancelar
                </Box>
                <Box component="button" type="button" onClick={saveNote} disabled={!noteText.trim() || saving}
                  sx={{
                    ...foco, height: 38, px: '18px', borderRadius: '999px', border: 0, bgcolor: 'var(--ch-green)', color: 'var(--cx-on-green)',
                    fontFamily: OUTFIT, fontSize: 14.5, fontWeight: 600, cursor: 'pointer', '&:disabled': { opacity: 0.5, cursor: 'default' },
                  }}>
                  Salvar
                </Box>
              </Box>
            )}
          </Box>
        )}

        {tab === 'hist' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Secao mb={0}>Últimas interações</Secao>
            {timeline.length === 0 && <Box sx={textoVazio}>Sem mensagens registradas ainda.</Box>}
            {timeline.map((ev, i) => {
              const cliente = ev.direction === 'INBOUND';
              return (
                <Box key={i} sx={{ display: 'flex', gap: '12px' }}>
                  <Box component="span" aria-hidden sx={{
                    width: 34, height: 34, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: cliente ? 'var(--ch-green-bg)' : 'var(--ch-blue-bg)', color: cliente ? 'var(--ch-green-d)' : 'var(--ch-blue-d)',
                    border: `1px solid ${cliente ? 'var(--ch-green-b)' : 'var(--ch-blue-b)'}`,
                  }}>
                    <FiMessageCircle size={15} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, p: '12px 14px', borderRadius: '4px 14px 14px 14px', bgcolor: 'var(--ch-surf)', border: '1px solid var(--ch-bdr)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Box component="span" sx={{ fontSize: 14, fontWeight: 600, color: 'var(--ch-text2)' }}>{cliente ? 'Cliente' : (ev.user_name || 'Sistema')}</Box>
                      <Box component="span" sx={{ fontSize: 13, color: 'var(--cx-cap)', ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>{fmtDate(ev.timestamp)}</Box>
                    </Box>
                    <Box component="span" sx={{ fontSize: 15, lineHeight: 1.55, overflowWrap: 'anywhere' }}>{ev.content}</Box>
                  </Box>
                </Box>
              );
            })}
            <Box sx={{ height: '1px', bgcolor: 'var(--ch-bdr)', my: '8px' }} />
            <Box component="span" sx={{ fontSize: 13.5, color: 'var(--cx-cap)', textAlign: 'center' }}>
              Timeline completa (mudanças de status, atribuições, anotações) chega em breve.
            </Box>
          </Box>
        )}

        {/* Formulários PR 2 (out/26): o que este contato respondeu. */}
        {tab === 'forms' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Secao hint="o que já estava no CRM não é sobrescrito" mb={0}>Formulários</Secao>
            {formularios === null ? (
              <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={18} sx={{ color: 'var(--ch-green)' }} /></Box>
            ) : !formularios.length ? (
              <Box sx={textoVazio}>
                Nenhum formulário enviado a este contato. Envie um pela conversa, no botão de formulário.
              </Box>
            ) : (
              formularios.map((f) => (
                <Box key={f.id} sx={{ ...cartao, p: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
                    <Box component="span" sx={{ fontSize: 15.5, fontWeight: 600, flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                      {f.flow_nome || 'Formulário'}
                    </Box>
                    <Box component="span" sx={{ fontSize: 13, color: 'var(--cx-cap)', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtDate(f.respondido_em || f.enviado_em)}
                    </Box>
                  </Box>
                  <Box component="span" sx={{ fontSize: 13.5, color: 'var(--cx-cap)' }}>
                    {f.origem === 'fora' ? 'Respondido por um formulário enviado fora da Cetto'
                      : `Enviado por ${f.enviado_por || 'alguém da equipe'}`}
                    {!f.respondido_em && ' · aguardando resposta'}
                    {f.vezes > 1 && ` · respondeu ${f.vezes} vezes, vale a última`}
                  </Box>
                  {f.respondido_em && <RespostasLista respostas={f.respostas} crm={f.crm} cores={CORES_FORM} />}
                </Box>
              ))
            )}
          </Box>
        )}
      </Box>

      {/* ── Seletores de etiqueta e pasta ────────────────────────────────
          Menus ancorados nos "+". Mostram só o que a company tem e o
          contato ainda NÃO tem — anexar algo que já está anexado seria um
          no-op confuso. Lista vazia vira aviso, não menu em branco. */}
      <MenuCrm anchorEl={labelAnchor} onClose={() => setLabelAnchor(null)} titulo="Adicionar etiqueta">
        {availableLabels.length === 0 && (
          <NotaMenu>{allLabels.length === 0 ? 'Nenhuma etiqueta cadastrada. Crie no chat, ao lado do nome do contato.' : 'Todas já aplicadas.'}</NotaMenu>
        )}
        {availableLabels.map(lb => (
          <ItemMenu key={lb.id} ponto={labelColor(lb.color).dot} onClick={() => { setLabelAnchor(null); onAttachLabel?.(lb.id); }}>
            {lb.name}
          </ItemMenu>
        ))}
      </MenuCrm>

      <MenuCrm anchorEl={folderAnchor} onClose={() => setFolderAnchor(null)} titulo="Adicionar a pasta">
        {availableFolders.length === 0 && (
          <NotaMenu>{allFolders.length === 0 ? 'Nenhuma pasta cadastrada.' : 'Já está em todas.'}</NotaMenu>
        )}
        {availableFolders.map(fd => (
          <ItemMenu key={fd.id} pasta={folderColor(fd.color).color} onClick={() => { setFolderAnchor(null); onAttachFolder?.(fd.id); }}>
            {fd.name}
          </ItemMenu>
        ))}
      </MenuCrm>

      {/* Rodapé fixo com o estado de gravação.
          Existe porque "salvou?" precisa ter resposta visível. Interesse,
          reunião e agente gravam na escolha (ação única); os campos
          personalizados são texto livre e ficam pendentes até este botão —
          ou até o modal fechar, que grava sozinho. */}
      <Box sx={{
        flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
        p: '12px 16px 12px 24px', borderTop: '1px solid var(--ch-bdr)',
        bgcolor: hasUnsaved ? 'var(--ch-amber-bg)' : 'var(--ch-surf)', transition: 'background-color .2s',
        [ESTREITO]: { pl: '16px' },
      }}>
        <Box component="span" aria-live="polite" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: 14, color: hasUnsaved ? 'var(--ch-amber-d)' : 'var(--cx-cap)' }}>
          {!hasUnsaved && !savingFields && <Box component="span" sx={{ display: 'flex', color: 'var(--ch-green)' }}><FiCheck size={15} strokeWidth={2.6} /></Box>}
          {statusRodape}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {hasUnsaved && !savingFields && (
            <Box component="button" type="button" onClick={() => setDrafts({})}
              sx={{ ...foco, height: 38, px: '14px', borderRadius: '999px', border: 0, bgcolor: 'transparent', color: 'var(--ch-text2)', fontFamily: OUTFIT, fontSize: 14.5, fontWeight: 500, cursor: 'pointer' }}>
              Descartar
            </Box>
          )}
          <Box component="button" type="button" onClick={saveFields} disabled={!hasUnsaved || savingFields}
            sx={{
              ...foco, minWidth: 96, height: 38, px: '18px', borderRadius: '999px', border: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              bgcolor: hasUnsaved ? 'var(--ch-green)' : 'var(--ch-inset)', color: hasUnsaved ? 'var(--cx-on-green)' : 'var(--cx-cap)',
              fontFamily: OUTFIT, fontSize: 14.5, fontWeight: 600, cursor: hasUnsaved && !savingFields ? 'pointer' : 'default', transition: 'background-color .15s',
            }}>
            {savingFields && <CircularProgress size={12} color="inherit" />}
            {savingFields ? 'Salvando' : 'Salvar'}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
